import React, { useState, useCallback, useEffect, useRef } from 'react';
import { SearchForm } from '../../components/searchForm';
import { TypeDropdown } from '../../components/radioButtons';
import PriceRangeSlider from '../../components/priceRangeSlider';
import DateRangeSlider from '../../components/dateRangeSlider';
import { usePeoplesPanelData } from './usePeoplesPanelData';
import { InteractiveMap } from './InteractiveMap';
import SEOComponent from '../../components/SEOComponent';
import { graphqlOperation } from '@aws-amplify/api-graphql';
import { generateClient } from '@aws-amplify/api';
import { voteOnSubmission } from '../../graphql/mutations';
import { useSubscription } from '../../components/RequireSubscription';
import { MAPBOX_STYLES, COLOUR_SCALE_PALETTES } from '../PPMap/constants';
import { toSliderScale, fromSliderScale, toDateSliderScale, fromDateSliderScale } from '../PPMap/utils';
import SubmissionModal from './SubmissionModal';
import './PeoplesIndexMap.css';

const client = generateClient();


async function fetchLocation(normalizedQuery) {
  const stub = normalizedQuery.match(/[a-zA-Z]+/g)[0];
  const url = `postcode/${stub}.json`;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    }
    const locationData = await response.json();
    const location = locationData[normalizedQuery];
    return location;
  } catch (error) {
    return null;
  }
}

function PeoplesIndexMap() {
  const { userEmail, isAuthenticated } = useSubscription();

  const [searchQuery, setSearchQuery] = useState('');
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [mapKey, setMapKey] = useState(0);
  const [radioValue, setRadioValue] = useState('All');
  const [zoom, setZoom] = useState(6.5);
  const [center, setCenter] = useState({ lat: 51.748354, lon: -1.251952 });
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [clickedLocation, setClickedLocation] = useState(null);

  const { isLoading, mapData, colorBarRange, fetchMapData } = usePeoplesPanelData(searchQuery);

  const absoluteMaxPrice = mapData.length > 0 ? Math.max(...mapData.map(d => d.price)) * 1.2 : 50000000;
  const absoluteMinPrice = 3000;
  const currentDate = new Date();
  const stringMaxDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0).toISOString().split('T')[0];

  const [minPrice, setMinPrice] = useState(absoluteMinPrice);
  const [maxPrice, setMaxPrice] = useState(absoluteMaxPrice);
  const [minDate, setMinDate] = useState('2020-01-01');
  const [maxDate, setMaxDate] = useState(stringMaxDate);
  const [filteredMapData, setFilteredMapData] = useState([]);
  const [mapboxStyleIndex, setMapboxStyleIndex] = useState(0);
  const [colourScaleIndex, setColourScaleIndex] = useState(0);

  const currentMapboxStyle = MAPBOX_STYLES[mapboxStyleIndex];
  const currentColourScale = COLOUR_SCALE_PALETTES[colourScaleIndex];

  const changeMapboxStyle = () => {
    const newIndex = (mapboxStyleIndex + 1) % MAPBOX_STYLES.length;
    const newColourScaleIndex = (colourScaleIndex + 1) % COLOUR_SCALE_PALETTES.length;
    setMapboxStyleIndex(newIndex);
    setColourScaleIndex(newColourScaleIndex);
  };

  useEffect(() => {
    const minDateTimestamp = new Date(minDate).getTime();
    const maxDateTimestamp = new Date(maxDate).getTime();
    const filteredData = mapData.filter(item =>
      item.price >= minPrice &&
      item.price <= maxPrice &&
      item.date >= minDateTimestamp &&
      item.date <= maxDateTimestamp
    );
    setFilteredMapData(filteredData);
  }, [mapData, minPrice, maxPrice, minDate, maxDate]);

  const updateMapView = useCallback(({ zoom, center }) => {
    setZoom(zoom.toFixed(3));
    setCenter(center);
  }, []);

  const handleSearch = useCallback(async () => {
    setIsButtonDisabled(true);
    try {
      const normalizedQuery = searchQuery.replace(/\s+/g, "").toUpperCase();
      const location = await fetchLocation(normalizedQuery);

      if (location) {
        setMapKey((prevKey) => prevKey + 1);
        setCenter({ lat: location.latitude, lon: location.longitude });
        setZoom(14);
      }
    } catch (error) {
      console.error('Search error:', error?.message || 'unknown');
    } finally {
      setIsButtonDisabled(false);
    }
  }, [searchQuery]);

  // Only fetch data when radioValue changes, not on every pan/zoom
  const hasInitiallyFetchedRef = useRef(false);

  useEffect(() => {
    if (zoom && center && radioValue && !hasInitiallyFetchedRef.current) {
      fetchMapData(zoom, center.lat, center.lon, radioValue);
      hasInitiallyFetchedRef.current = true;
    }
  }, [zoom, center, radioValue, fetchMapData]);

  useEffect(() => {
    if (hasInitiallyFetchedRef.current && zoom && center) {
      fetchMapData(zoom, center.lat, center.lon, radioValue);
    }
  }, [radioValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMinPriceChange = useCallback((value) => {
    const newPrice = fromSliderScale(value, absoluteMinPrice, absoluteMaxPrice);
    setMinPrice(newPrice);
  }, [absoluteMinPrice, absoluteMaxPrice]);

  const handleMaxPriceChange = useCallback((value) => {
    const newPrice = fromSliderScale(value, absoluteMinPrice, absoluteMaxPrice);
    setMaxPrice(newPrice);
  }, [absoluteMinPrice, absoluteMaxPrice]);

  const handleMinDateChange = useCallback((value) => {
    const newDate = fromDateSliderScale(value, '2020-01-01', stringMaxDate);
    setMinDate(newDate);
  }, [stringMaxDate]);

  const handleMaxDateChange = useCallback((value) => {
    const newDate = fromDateSliderScale(value, '2020-01-01', stringMaxDate);
    setMaxDate(newDate);
  }, [stringMaxDate]);

  const handleRadioChange = (value) => {
    setRadioValue(value);
  };

  const toggleVisibility = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleVote = useCallback(async (submission_id, vote) => {
    if (!isAuthenticated || !userEmail) {
      alert('Please log in to vote');
      return;
    }

    try {
      const input = { submission_id, email: userEmail, vote };
      const response = await client.graphql(
        graphqlOperation(voteOnSubmission, { input })
      );

      if (response.errors && response.errors.length > 0) {
        alert(`Vote failed: ${response.errors[0].message}`);
        return;
      }

      if (!response.data || !response.data.voteOnSubmission) {
        alert('Failed to record vote: No response from server');
        return;
      }

      if (response.data.voteOnSubmission.success) {
        const { score: newScore, upvotes: newUpvotes, downvotes: newDownvotes } = response.data.voteOnSubmission;

        setFilteredMapData(prevData =>
          prevData.map(item =>
            item.submission_id === submission_id
              ? { ...item, upvotes: newUpvotes, downvotes: newDownvotes, score: newScore }
              : item
          )
        );

        fetchMapData(zoom, center.lat, center.lon, radioValue);
      } else {
        alert('Failed to record vote: ' + response.data.voteOnSubmission.message);
      }
    } catch (error) {
      console.error('Vote error:', error?.message || 'unknown');
      alert('Error recording vote: ' + (error.message || 'Unknown error'));
    }
  }, [isAuthenticated, userEmail, fetchMapData, zoom, center, radioValue]);

  const handleMapClick = useCallback((event) => {
    if (!isAuthenticated) return;
    if (event && event.points && event.points.length > 0) return;

    if (event && event.latlng) {
      setClickedLocation({ lat: event.latlng.lat, lon: event.latlng.lng });
      setShowSubmissionModal(true);
    }
  }, [isAuthenticated]);


  return (
    <>
      <SEOComponent
        title="People's Property Index - Crowdsourced Property Transactions"
        description="Browse crowdsourced property transaction data submitted by the community. Contribute to the People's Property Index."
        keywords="property prices, crowdsourced data, UK property, house prices, community index"
      />

      <div className="parent-container">
        <div className="options-container">
          <button className="toggle-button" onClick={toggleVisibility}>
            {isCollapsed ? 'Show Options' : 'Hide Options'}
          </button>
          <button className="map-style-button" onClick={changeMapboxStyle}>
            🎨
          </button>
          <div className={`search-container ${isCollapsed ? 'hidden' : 'visible'}`}>
            <SearchForm
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              handleSearch={handleSearch}
              isButtonDisabled={isButtonDisabled}
            />

            <TypeDropdown
              radioValue={radioValue}
              onChange={handleRadioChange}
            />

            <PriceRangeSlider
              min_label={`£${(Math.round(minPrice / 10) * 10).toFixed(0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`}
              max_label={`£${(Math.round(maxPrice / 10) * 10).toFixed(0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`}
              min={1}
              max={1000}
              value1={toSliderScale(minPrice, absoluteMinPrice, absoluteMaxPrice)}
              value2={toSliderScale(maxPrice, absoluteMinPrice, absoluteMaxPrice)}
              onChange1={handleMinPriceChange}
              onChange2={handleMaxPriceChange}
              mapData={mapData}
              setFilteredData={setFilteredMapData}
            />

            <DateRangeSlider
              min_label={minDate}
              max_label={maxDate}
              min={1}
              max={1000}
              value1={toDateSliderScale(minDate, '2020-01-01', stringMaxDate)}
              value2={toDateSliderScale(maxDate, '2020-01-01', stringMaxDate)}
              onChange1={handleMinDateChange}
              onChange2={handleMaxDateChange}
              mapData={mapData}
              setFilteredData={setFilteredMapData}
            />
          </div>
        </div>


        <div className="map-container">
          {isLoading && <div className="loading-indicator">Loading data...</div>}

        <InteractiveMap
          key={mapKey}
          mapData={filteredMapData || []}
          mapCenter={center}
          zoomLevel={zoom}
          updateMapView={updateMapView}
          mapboxStyle={currentMapboxStyle}
          colorscalePalette={currentColourScale}
          colorBarRange={colorBarRange}
          onMapClick={handleMapClick}
          userEmail={userEmail}
          isAuthenticated={isAuthenticated}
          onVote={handleVote}
        />
        </div>
      </div>

      {showSubmissionModal && (
        <SubmissionModal
          isOpen={showSubmissionModal}
          onClose={() => {
            setShowSubmissionModal(false);
            setClickedLocation(null);
          }}
          userEmail={userEmail}
          initialLocation={clickedLocation}
        />
      )}
    </>
  );
}

export default PeoplesIndexMap;
